import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface PaywallModalProps {
  isOpen: boolean;
  freeRegion: string | null;
  onClose: () => void;
  /**
   * Chiamata quando l'utente conferma l'acquisto.
   * In dev/web mostra alert e simula sblocco. Sul build Capacitor
   * invoca RevenueCat → sul successo chiama markPurchasedAll().
   */
  onPurchase: () => Promise<void>;
  onRestore: () => Promise<void>;
}

/**
 * Paywall modal: appare quando l'utente clicca un POI di una regione
 * non sbloccata oppure dal menu "Sblocca tutte le regioni".
 * Mostra il valore offerto + bottone "Acquista €7,99" + "Restore purchase".
 */
export const PaywallModal: React.FC<PaywallModalProps> = ({
  isOpen,
  freeRegion,
  onClose,
  onPurchase,
  onRestore,
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleEsc);
    closeBtnRef.current?.focus();
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  if (!isOpen) return null;

  const handlePurchase = async () => {
    setBusy(true);
    setError(null);
    try {
      await onPurchase();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Acquisto fallito. Riprova piu' tardi.");
    } finally {
      setBusy(false);
    }
  };

  const handleRestore = async () => {
    setBusy(true);
    setError(null);
    try {
      await onRestore();
      // L'effetto secondario: se restore va a buon fine, lo stato cambia
      // e il componente parent chiuderà il paywall.
    } catch (e) {
      setError(e instanceof Error ? e.message : "Restore fallito.");
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/70 backdrop-blur-md p-4 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="paywall-title"
      onClick={onClose}
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header con gradient teal */}
        <div
          className="px-6 pt-6 pb-5 text-white relative"
          style={{ background: "linear-gradient(135deg, #06b6d4 0%, #0E7490 100%)" }}
        >
          <button
            ref={closeBtnRef}
            onClick={onClose}
            className="absolute top-3 right-3 text-white/80 hover:text-white p-1"
            aria-label="Chiudi"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <h2 id="paywall-title" className="text-2xl font-bold leading-tight" style={{ fontFamily: "Georgia, serif" }}>
            Sblocca tutta Italia
          </h2>
          <p className="text-cyan-50 text-sm mt-1">
            {freeRegion
              ? <>Stai esplorando gratis <strong>{freeRegion}</strong>. Sblocca le altre 19 regioni.</>
              : <>Sblocca tutti i 1.200+ locali delle migliori guide d'Italia.</>}
          </p>
        </div>

        {/* Value props */}
        <div className="px-6 py-5 space-y-3">
          <FeatureRow text="Oltre 1.200 locali selezionati in tutta Italia" />
          <FeatureRow text="Aggregazione delle principali guide gastronomiche italiane" />
          <FeatureRow text="Indirizzo, telefono, sito web cliccabili" />
          <FeatureRow text="Acquisto unico, nessun abbonamento" />
        </div>

        {/* CTA price */}
        <div className="px-6 pb-4">
          <button
            type="button"
            onClick={handlePurchase}
            disabled={busy}
            className="w-full bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-300 disabled:cursor-wait text-white font-bold py-4 px-4 rounded-xl transition-colors text-lg shadow-md"
          >
            {busy ? "Elaborazione..." : "Sblocca tutto — €7,99"}
          </button>
          <button
            type="button"
            onClick={handleRestore}
            disabled={busy}
            className="w-full text-cyan-700 hover:text-cyan-600 disabled:text-gray-400 text-sm font-semibold py-2 mt-2 transition-colors"
          >
            Hai gia' acquistato? Ripristina
          </button>
          {error && (
            <p className="text-sm text-red-600 mt-2 text-center">{error}</p>
          )}
        </div>

        {/* Footer micro-copy */}
        <p className="px-6 pb-4 text-xs text-gray-500 text-center leading-snug">
          Pagamento sicuro tramite App Store / Google Play. Nessun rinnovo automatico.
        </p>
      </div>
    </div>,
    document.body
  );
};

const FeatureRow: React.FC<{ text: string }> = ({ text }) => (
  <div className="flex items-start gap-2 text-sm text-gray-800">
    <svg className="w-5 h-5 text-cyan-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
    <span>{text}</span>
  </div>
);
